import { useEffect, useMemo, useState, type ChangeEvent, type CSSProperties, type ReactNode } from "react";
import { Btn, Icon, Input, Pill, IconBtn, T } from "@v0y4ge/jrni-ui";
import { getEventShare, updateEventVisibility } from "@v0y4ge/api-client";
import { toast } from "../../../shell/toast";
import type { UIEvent } from "../../../lib/events-adapter";

/* ───────────────────────────────────────────────────────────────────────
 * EventShareTab — public/private event URL, slug customization, UTM builder,
 * and saved tracked-link variants.
 *
 * UIR-06 round 3 (2026-08-05, operator): UTM parameters render ONE PER ROW with
 * the description as helper text UNDER the field (it used to sit in the
 * placeholder, where it read like a pre-filled value), plus `utm_id` — a new
 * parameter added to the `EventShareUtm` contract this same date. The QR button
 * is live: the symbol is encoded in-app (`lib/qrcode.ts` → `shell/QrCode.tsx`,
 * already used by hub/landing pages) from the REAL tracked URL and downloads as
 * a 1024 px PNG, so no server op is needed for it.
 *
 * SERVING (same date): `getEventShare` (GET /v1/admin/events/{id}/share) and
 * `updateEventVisibility` (PATCH; visibility · slug · inviteToken · refName ·
 * utm · savedLinks) are declared ops that NOTHING served in the design
 * environment — both answered 501 ROUTE_UNBOUND, so this tab showed its
 * "couldn't load" banner and every "Save variant" silently failed. They are now
 * bound in the serving engine against the seed-world event row, with operator
 * writes persisted per event, so variants really do save and survive reload.
 * Round 1 stripped two fabricated tracked-link rows; click counts are still
 * whatever the server reports and nothing here is invented.
 * Backend follow-up for Claude Code: REQ-127 (utm_id column, share-record
 * persistence, optional server-rendered/branded QR + short links).
 * ─────────────────────────────────────────────────────────────────────── */
import { publicHost, useTenantPublicConfig } from "../../../lib/public-url";
import { QrCode, downloadQrPng } from "../../../shell/QrCode";
import { useT, t as i18nT } from "../../../lib/i18n";
/* ─────────────────────────────────────────────────────────────────────── */
// Tenant-scoped public host via the shared composer (was hardcoded jrni.events).
/* One row per parameter, following the treatment the operator prefers from
 * Calenso (2026-08-05): sentence-case "UTM Source:" label, full-width field, and
 * TWO lines of helper text under it — an "Optional. <what it does>" line and an
 * example line. The examples replace the old preset chips (same values, less
 * chrome); the description used to sit in the placeholder, where it read like a
 * pre-filled value. Order matches Calenso's: source → medium → campaign → term
 * → content → id. `slugCase: false` keeps utm_id's own casing: campaign IDs are
 * opaque strings from the ad platform, not slugs. */
const UTM_ROWS: { field: keyof Utm; labelKey: string; descKey: string; exampleKey: string; slugCase?: boolean }[] = [
  { field: "source", labelKey: "events.detailShareTab.labelUtmSource", descKey: "events.detailShareTab.descUtmSource", exampleKey: "events.detailShareTab.exUtmSource" },
  { field: "medium", labelKey: "events.detailShareTab.labelUtmMedium", descKey: "events.detailShareTab.descUtmMedium", exampleKey: "events.detailShareTab.exUtmMedium" },
  { field: "campaign", labelKey: "events.detailShareTab.labelUtmCampaign", descKey: "events.detailShareTab.descUtmCampaign", exampleKey: "events.detailShareTab.exUtmCampaign" },
  { field: "term", labelKey: "events.detailShareTab.labelUtmTerm", descKey: "events.detailShareTab.descUtmTerm", exampleKey: "events.detailShareTab.exUtmTerm" },
  { field: "content", labelKey: "events.detailShareTab.labelUtmContent", descKey: "events.detailShareTab.descUtmContent", exampleKey: "events.detailShareTab.exUtmContent" },
  { field: "id", labelKey: "events.detailShareTab.labelUtmId", descKey: "events.detailShareTab.descUtmId", exampleKey: "events.detailShareTab.exUtmId", slugCase: false },
];

function slugify(s: string): string {
  return (s || "").toString().toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
}

type Utm = { id: string; source: string; medium: string; campaign: string; content: string; term: string };
const EMPTY_UTM: Utm = { id: "", source: "", medium: "", campaign: "", content: "", term: "" };
const UTM_ORDER = ["id", "source", "medium", "campaign", "content", "term"] as const;
type SavedLink = { id: string; label: string; url: string; clicks: number; created: string; createdAt: string; ref?: string; utm?: Utm };

/** "" until the attend host is known: a link is never built on a guessed host (CC, 3 Oct). */
function buildShareUrl({ host, visibility, slug, inviteToken, ref, utm }: {
  host: string; visibility: string; slug: string; inviteToken?: string; ref?: string; utm?: Partial<Utm>;
}): string {
  if (!host) return "";
  const params = new URLSearchParams();
  if (visibility === "private" && inviteToken) params.set("inv", inviteToken);
  if (ref) params.set("ref", ref);
  (UTM_ORDER).forEach((k) => {
    if (utm && utm[k]) params.set(`utm_${k}`, utm[k]!);
  });
  const qs = params.toString();
  return `https://${host}/${slug || "event"}${qs ? `?${qs}` : ""}`;
}

function copyText(text: string, label?: string) {
  const done = () => toast({ title: label || i18nT("events.detailShareTab.linkCopied", "Link copied"), tone: "success" });
  try {
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(done, done);
    else done();
  } catch { done(); }
}

function randToken(n: number): string {
  const a = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
  let out = "";
  for (let i = 0; i < n; i += 1) out += a[Math.floor(Math.random() * a.length)];
  return out;
}

const inlineLinkBtn: CSSProperties = { background: "none", border: "none", padding: 0, color: T.p1, fontFamily: T.font, fontSize: 12, fontWeight: 600, cursor: "pointer", textDecoration: "underline" };
const mono = "JetBrains Mono, monospace";

function ShareSection({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <section>
      <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: T.n1, textTransform: "uppercase", letterSpacing: 0.04 }}>{title}</h3>
      {subtitle ? <p style={{ margin: "4px 0 12px", fontSize: 12, color: T.soft, lineHeight: 1.5 }}>{subtitle}</p> : null}
      {children}
    </section>
  );
}

function VisibilityCard({ active, onClick, icon, title, body, disabled }: { active: boolean; onClick: () => void; icon: ReactNode; title: string; body: string; disabled?: boolean }) {
  return (
    <Btn variant="secondary" onClick={onClick} disabled={disabled} style={{ textAlign: "left", padding: 12, borderRadius: 6, height: "auto", whiteSpace: "normal", background: active ? T.p4 : T.white, border: `1px solid ${active ? T.p1 : T.border}`, display: "flex", flexDirection: "column", alignItems: "stretch", gap: 6, color: T.n1, boxShadow: "none" }}>
      <span style={{ width: 26, height: 26, borderRadius: 6, background: active ? T.p1 : T.n6, color: active ? T.white : T.n2, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{icon}</span>
      <div style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{title}</div>
      <div style={{ fontSize: 11, color: T.soft, lineHeight: 1.45 }}>{body}</div>
    </Btn>
  );
}

function UtmField({ label, field, utm, setUtm, description, example, slugCase = true }: {
  label: string; field: keyof Utm; utm: Utm; setUtm: (fn: (u: Utm) => Utm) => void; description: string; example: string; slugCase?: boolean;
}) {
  return (
    <Input label={label} value={utm[field]} size="sm"
      onChange={(e: ChangeEvent<HTMLInputElement>) => setUtm((u) => ({ ...u, [field]: slugCase ? slugify(e.target.value) : e.target.value.trim() }))}
      help={<span style={{ display: "flex", flexDirection: "column", gap: 2 }}><span>{description}</span><span>{example}</span></span>} />
  );
}

export function EventShareTab({ ev, onOpenLanding }: { ev: UIEvent; onOpenLanding: () => void }) {
  const t = useT();
  const [visibility, setVisibility] = useState("public");
  /** An ended event (completed or archived) takes no registrations and its share settings are read-only. */
  const ended = ev.status === "past";
  /** An archived event's public page is gone (the public event lookup refuses archived events), so no link to
   *  it is offered; a completed event's page still renders. Both are "past"; the server status tells them apart. */
  const archived = ended && (ev.raw as { status?: string } | undefined)?.status === "archived";
  const [slug, setSlug] = useState(() => slugify(ev.title) || ev.id);
  const [inviteToken, setInviteToken] = useState(() => randToken(8));
  const [refName, setRefName] = useState("");
  const [utm, setUtm] = useState<Utm>(EMPTY_UTM);
  const [showUtm, setShowUtm] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [saved, setSaved] = useState<SavedLink[]>([]);
  const [busy, setBusy] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  // Hydrate from the real share record.
  useEffect(() => {
    let alive = true;
    getEventShare(ev.id).then((cfg: any) => {
      if (!alive || !cfg) return;
      setVisibility(cfg.visibility ?? "public");
      if (cfg.slug) setSlug(cfg.slug);
      if (cfg.inviteToken) setInviteToken(cfg.inviteToken);
      setRefName(cfg.refName ?? "");
      if (cfg.utm) setUtm({ id: cfg.utm.id ?? "", source: cfg.utm.source ?? "", medium: cfg.utm.medium ?? "", campaign: cfg.utm.campaign ?? "", content: cfg.utm.content ?? "", term: cfg.utm.term ?? "" });
      setSaved(Array.isArray(cfg.savedLinks) ? cfg.savedLinks : []);
      setLoadFailed(false);
    }).catch((e: unknown) => {
      console.warn("getEventShare failed:", e);
      if (alive) setLoadFailed(true);
    });
    return () => { alive = false; };
  }, [ev.id]);

  /* The served public config names the attend host; until it loads (or when it cannot be
     read) there is no host, so no link, copy or QR code is offered (CC, 3 Oct). */
  const publicCfg = useTenantPublicConfig();
  const attendHost = publicCfg ? publicHost("attend") : "";
  const canonicalUrl = useMemo(() => buildShareUrl({ host: attendHost, visibility, slug, inviteToken, ref: "", utm: {} }), [attendHost, visibility, slug, inviteToken]);
  const trackedUrl = useMemo(() => buildShareUrl({ host: attendHost, visibility, slug, inviteToken, ref: slugify(refName), utm }), [attendHost, visibility, slug, inviteToken, refName, utm]);
  const hasAnyTracking = !!slugify(refName) || Object.values(utm).some(Boolean);

  /** Persist a patch to the share record; reverts nothing locally on failure
   *  beyond re-reading, and never claims success it didn't get. */
  const persist = async (patch: Record<string, unknown>, okTitle?: string): Promise<boolean> => {
    setBusy(true);
    try {
      const cfg = await updateEventVisibility(ev.id, { visibility, slug, inviteToken, refName, utm, savedLinks: saved, ...patch } as any) as any;
      if (cfg) {
        setVisibility(cfg.visibility ?? visibility);
        if (cfg.slug) setSlug(cfg.slug);
        if (cfg.inviteToken) setInviteToken(cfg.inviteToken);
        setSaved(Array.isArray(cfg.savedLinks) ? cfg.savedLinks : []);
      }
      if (okTitle) toast({ title: okTitle, tone: "success" });
      return true;
    } catch (e) {
      console.warn("updateEventVisibility failed:", e);
      toast({ title: t("events.detailShareTab.couldnTSaveShareSettings", "Couldn't save share settings"), subtitle: t("events.detailShareTab.nothingWasSavedRetryInAMoment", "Nothing was saved — retry in a moment."), tone: "danger" });
      return false;
    } finally { setBusy(false); }
  };

  const changeVisibility = (next: string) => {
    if (ended) return;
    const prev = visibility;
    setVisibility(next);
    void persist({ visibility: next }).then((ok) => { if (!ok) setVisibility(prev); });
  };
  const regenerateToken = () => {
    const next = randToken(8);
    const prev = inviteToken;
    setInviteToken(next);
    void persist({ inviteToken: next }).then((ok) => { if (!ok) setInviteToken(prev); });
  };

  const saveTrackedLink = async () => {
    if (!hasAnyTracking || busy) return;
    const label = slugify(refName) || (utm.campaign && slugify(utm.campaign)) || `variant-${saved.length + 1}`;
    const now = new Date().toISOString();
    const variant: SavedLink = { id: `v${Date.now()}`, label, url: trackedUrl, clicks: 0, created: now, createdAt: now, ref: slugify(refName) || undefined, utm };
    const ok = await persist({ savedLinks: [variant, ...saved], refName: "", utm: EMPTY_UTM }, t("events.detailShareTab.trackedLinkSaved", "Tracked link saved"));
    if (ok) {
      setRefName("");
      setUtm(EMPTY_UTM);
      setShowUtm(false);
    }
  };
  const removeTrackedLink = (id: string) => {
    void persist({ savedLinks: saved.filter((x) => x.id !== id) });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {loadFailed ? (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "10px 12px", border: `1px solid ${T.border}`, borderRadius: 6, background: T.n6 }}>
          <Icon.warn size={13} />
          <div style={{ fontSize: 12, color: T.n2, lineHeight: 1.5 }}>{t("events.detailShareTab.couldnTLoadTheSavedShareSettings", "Couldn't load the saved share settings for this event — what's shown below hasn't been read from the server, and changes may not save.")}</div>
        </div>
      ) : null}
      <ShareSection title={t("events.detailShareTab.whoCanAccessThisLink", "Who can access this link")} subtitle={t("events.detailShareTab.publicLinksWorkForAnyonePrivateLinksCarryAnInviteTokenAnd", "Public links work for anyone. Private links carry an invite token and can be revoked.")}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <VisibilityCard active={visibility === "public"} disabled={ended} onClick={() => changeVisibility("public")} icon={<Icon.globe size={16} />} title={t("events.detailShareTab.public", "Public")} body={archived ? t("events.detailShareTab.publicArchived", "This event is archived, so its public page is no longer available.") : ended ? t("events.detailShareTab.publicEnded", "Anyone with the link can see this event. It has ended, so registration is closed.") : t("events.detailShareTab.anyoneWithTheLinkCanRegisterUseOnSocialAdsAndPartners", "Anyone with the link can register. Use on social, ads, and partners.")} />
          <VisibilityCard active={visibility === "private"} disabled={ended} onClick={() => changeVisibility("private")} icon={<Icon.lock size={14} />} title={t("events.detailShareTab.private", "Private")} body={archived ? t("events.detailShareTab.privateArchived", "This event is archived, so its invite links no longer open it.") : ended ? t("events.detailShareTab.privateEnded", "Guests with an invite token can see this event. It has ended, so registration is closed.") : t("events.detailShareTab.requiresAnInviteTokenEachGuestCanGetAPersonalToken", "Requires an invite token. Each guest can get a personal token.")} />
        </div>
        {visibility === "private" ? (
          <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", background: T.n6, borderRadius: 6, fontSize: 12, color: T.n2 }}>
            <Icon.lock size={12} />
            <span style={{ flex: 1 }}>{t("events.detailShareTab.token", "Token")}{" "}<code style={{ fontFamily: mono, fontSize: 12, color: T.n1, fontWeight: 600 }}>{inviteToken}</code> {t("events.detailShareTab.text1UsePerRecipient", "· 1 use per recipient")}</span>
            <Btn variant="link" onClick={regenerateToken} disabled={busy || ended} style={{ ...inlineLinkBtn, height: "auto", opacity: 1 }}>{t("events.detailShareTab.regenerate", "Regenerate")}</Btn>
          </div>
        ) : null}
      </ShareSection>

      <ShareSection title={t("events.detailShareTab.eventUrl", "Event URL")} subtitle={t("events.detailShareTab.theCanonicalLinkForThisEventEditThePathToMakeIt", "The canonical link for this event. Edit the path to make it memorable.")}>
        <div style={{ display: "flex", alignItems: "stretch", border: `1px solid ${T.border}`, borderRadius: 6, overflow: "hidden", background: T.white }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "0 12px", background: T.n6, color: T.soft, fontFamily: mono, fontSize: 13, borderRight: `1px solid ${T.border}` }}>
            <Icon.link size={12} />{attendHost || "…"}/
          </span>
          {/* ui-raw: borderless inline slug input sharing one bordered row with the host prefix (mono 13px); Input's own bordered wrapper + fixed font can't reproduce the composed URL field */}
          <input value={slug} readOnly={ended} aria-readonly={ended || undefined} onChange={(e) => setSlug(slugify(e.target.value))} onBlur={() => { if (!ended && slug.trim()) void persist({ slug }); }} placeholder={t("events.detailShareTab.eventSlug", "event-slug")} style={{ flex: 1, border: "none", outline: "none", padding: "0 12px", fontFamily: mono, fontSize: 13, color: T.n1, background: T.white, minWidth: 0 }} />
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
          <code style={{ flex: 1, minWidth: 0, fontFamily: mono, fontSize: 11, color: T.soft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textDecoration: archived ? "line-through" : undefined }}>{canonicalUrl || "…"}</code>
          <Btn variant="secondary" size="sm" icon={<Icon.copy size={12} />} disabled={!canonicalUrl || archived} onClick={() => copyText(canonicalUrl, t("events.detailShareTab.eventUrlCopied", "Event URL copied"))}>{t("events.detailShareTab.copy", "Copy")}</Btn>
          <Btn variant="secondary" size="sm" icon={<Icon.external size={12} />} disabled={archived} onClick={onOpenLanding}>{t("common.actions.open", "Open")}</Btn>
        </div>
      </ShareSection>

      <ShareSection title={t("events.detailShareTab.customizeATrackedLink", "Customize a tracked link")} subtitle={t("events.detailShareTab.addAReferenceNameAndUtmTagsSoYouCanAttributeRegistrations", "Add a reference name and UTM tags so you can attribute registrations back to the channel.")}>
        <Input label={t("events.detailShareTab.referenceWhereWillThisLinkBeUsed", "Reference (where will this link be used?)")} placeholder={t("events.detailShareTab.eGLobbyPosterAdvisorEmilyBranchToronto", "e.g. lobby-poster, advisor-emily, branch-toronto")} value={refName} onChange={(e: ChangeEvent<HTMLInputElement>) => setRefName(e.target.value)} help={t("events.detailShareTab.becomesRefOnTheUrl", "Becomes ?ref=… on the URL. Spaces and capitals are slugified automatically.")} />
        <Btn variant="link" onClick={() => setShowUtm((v) => !v)} style={{ marginTop: 12, padding: "8px 0", height: "auto", background: "none", whiteSpace: "normal", display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: T.p1, fontWeight: 600 }}>
          <span style={{ transform: showUtm ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 120ms", display: "inline-flex" }}><Icon.arrow dir="right" size={12} /></span>
          {t("events.detailShareTab.utmParameters", "UTM parameters")}{" "}{hasAnyTracking ? <Pill tone="primary">{Object.values(utm).filter(Boolean).length + (slugify(refName) ? 1 : 0)} {t("events.detailShareTab.set", "set")}</Pill> : null}
        </Btn>
        {showUtm ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}>
            {UTM_ROWS.map((r) => (
              <UtmField key={r.field} label={t(r.labelKey)} field={r.field} utm={utm} setUtm={setUtm}
                description={t(r.descKey)} example={t(r.exampleKey)} slugCase={r.slugCase} />
            ))}
          </div>
        ) : null}
        <div style={{ marginTop: 12, padding: 12, background: T.n6, borderRadius: 6, border: `1px dashed ${T.border}` }}>
          <div style={{ fontSize: 11, color: T.soft, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.04, marginBottom: 4 }}>{t("events.detailShareTab.preview", "Preview")}</div>
          <div style={{ fontFamily: mono, fontSize: 12, color: archived ? T.soft : T.n1, wordBreak: "break-all", lineHeight: 1.5, textDecoration: archived ? "line-through" : undefined }}>{trackedUrl || "…"}</div>
          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            <Btn size="sm" icon={<Icon.copy size={12} />} disabled={!trackedUrl || archived} onClick={() => copyText(trackedUrl, t("events.detailShareTab.trackedLinkCopied", "Tracked link copied"))}>{t("events.detailShareTab.copyLink", "Copy link")}</Btn>
            <Btn variant="secondary" size="sm" icon={<Icon.qr size={12} />} disabled={!trackedUrl || archived} onClick={() => setShowQr((v) => !v)} aria-expanded={showQr}>{showQr ? t("events.detailShareTab.hideQrCode", "Hide QR code") : t("events.detailShareTab.qrCode", "QR code")}</Btn>
            <Btn variant="secondary" size="sm" disabled={!hasAnyTracking || busy || !trackedUrl || archived} onClick={() => { void saveTrackedLink(); }} icon={<Icon.plus size={12} />}>{t("events.detailShareTab.saveVariant", "Save variant")}</Btn>
          </div>
          {showQr && trackedUrl && !archived ? (
            <div data-testid="share-qr-panel" style={{ marginTop: 12, padding: 14, background: T.white, border: `1px solid ${T.border}`, borderRadius: 6, display: "flex", gap: 14, alignItems: "center" }}>
              <QrCode value={trackedUrl} size={132} testid="share-qr" />
              <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: T.n2, lineHeight: 1.5 }}>{t("events.detailShareTab.scanToOpenTheTrackedLink", "Scan to open this tracked link. The PNG downloads print-ready at 1024 px.")}</div>
                <Btn variant="secondary" size="sm" icon={<Icon.download size={12} />} onClick={() => {
                  const ok = downloadQrPng(trackedUrl, `${slug || "event"}-qr.png`);
                  toast(ok
                    ? { title: t("events.detailShareTab.qrCodeGenerated", "QR code generated"), subtitle: t("events.detailShareTab.pngDownloadedToYourComputer", "PNG downloaded to your computer."), tone: "success" }
                    : { title: t("events.detailShareTab.qrCodeTooLong", "This link is too long to encode as a QR code"), tone: "danger" });
                }}>{t("events.detailShareTab.downloadPng", "Download PNG")}</Btn>
              </div>
            </div>
          ) : null}
        </div>
      </ShareSection>

      {saved.length > 0 ? (
        <ShareSection title={t("events.detailShareTab.savedTrackedLinks", "Saved tracked links")} subtitle={t("events.detailShareTab.clickTotalsComeFromTheServer", "Click totals are reported by the server.")}>
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
            {saved.map((v) => (
              <li key={v.id} style={{ padding: "10px 12px", border: `1px solid ${T.border}`, borderRadius: 6, display: "flex", alignItems: "center", gap: 10, background: T.white }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{v.label}</div>
                  <div style={{ fontFamily: mono, fontSize: 11, color: T.soft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textDecoration: archived ? "line-through" : undefined }}>{v.url}</div>
                  {archived ? <div data-testid="share-saved-link-dead" style={{ fontSize: 11, color: T.soft, marginTop: 2 }}>{t("events.detailShareTab.savedLinkNoLongerOpens", "No longer opens: this event is archived.")}</div> : null}
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{v.clicks.toLocaleString()}</div>
                  <div style={{ fontSize: 10, color: T.soft, textTransform: "uppercase", letterSpacing: 0.04 }}>{t("events.detailShareTab.clicks", "Clicks")}</div>
                </div>
                <IconBtn label={t("events.detailShareTab.copy", "Copy")} disabled={archived} onClick={() => copyText(v.url, t("events.detailShareTab.linkCopied", "Link copied"))}><Icon.copy size={12} /></IconBtn>
                <IconBtn label={t("common.actions.remove", "Remove")} disabled={ended} onClick={() => removeTrackedLink(v.id)}><Icon.x size={12} /></IconBtn>
              </li>
            ))}
          </ul>
        </ShareSection>
      ) : null}
    </div>
  );
}
